import React, { useMemo, useCallback } from "react";

import { useAuthorization } from "@common/providers";
import usePrivilegeCheck from "@common/hooks/usePrivilegeCheck";
import {
  useV2ListProjectVersionsQuery,
  useV2ListApprovalStepsForAccountQuery,
  useV2ListCollaboratorsForAccountQuery,
  useV2ListLineOfBusinessesForAccountQuery,
  useV2CreateProjectApprovalMutation,
  useV2ApproveProjectApprovalByIdMutation,
  useV2DeclineProjectApprovalByIdMutation,
  useV2CancelProjectApprovalByIdMutation,
  useV2RescopeProjectApprovalByIdMutation,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import {
  VersionSection,
  ApprovalStepSection,
  ApprovalRow,
} from "./types/ApprovalRow";
import Show from "./Show/Show";

interface Props {
  project_id: string;
}

const ProjectApprovalsV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;
  const appHost = process.env.REACT_APP_DOORKEEPER_APP_URL;

  const authorized = useAuthorization();
  const { accessLevel } = usePrivilegeCheck();
  // Default 'on' for backwards compatibility, as in the project header.
  const canCancel =
    (accessLevel("projects.request_approval") ?? "manage") === "manage";

  const { data: versionsData, isLoading: versionsLoading } =
    useV2ListProjectVersionsQuery({
      filter: { project: projectId },
      include: ["approval-steps"],
      page: { size: 500 },
    });

  const {
    data: stepsData,
    isLoading: stepsLoading,
    refetch: refetchSteps,
  } = useV2ListApprovalStepsForAccountQuery({
    filter: { project: projectId },
    include: ["project-approvals"],
    page: { size: 500 },
  });

  const { data: usersData, isLoading: usersLoading } =
    useV2ListCollaboratorsForAccountQuery({
      page: { size: 500 },
    });

  const { data: lobsData } = useV2ListLineOfBusinessesForAccountQuery({
    page: { size: 500 },
  });

  const [createProjectApproval] = useV2CreateProjectApprovalMutation();
  const [approveApproval] = useV2ApproveProjectApprovalByIdMutation();
  const [declineApproval] = useV2DeclineProjectApprovalByIdMutation();
  const [cancelApproval] = useV2CancelProjectApprovalByIdMutation();
  const [rescopeApproval] = useV2RescopeProjectApprovalByIdMutation();

  const users = useMemo(
    () =>
      (usersData?.data || []).map((u: any) => ({
        id: String(u.id),
        name: u.attributes?.name || "",
      })),
    [usersData]
  );

  const lobs = useMemo(
    () =>
      (lobsData?.data || []).map((l: any) => ({
        id: String(l.id),
        name: l.attributes?.name || "",
        checked: false,
      })),
    [lobsData]
  );

  const approvalsFromIncluded = useMemo(
    () =>
      ((stepsData?.included || []) as any[]).filter(
        (item: any) => item.type === "project-approvals"
      ),
    [stepsData]
  );

  const approvalsByStepId = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const approval of approvalsFromIncluded) {
      const stepId = String(
        approval.relationships?.["approval-step"]?.data?.id ?? ""
      );
      if (!stepId) continue;
      if (!map[stepId]) map[stepId] = [];
      map[stepId].push(approval);
    }
    return map;
  }, [approvalsFromIncluded]);

  const versions = useMemo((): VersionSection[] => {
    if (!versionsData?.data || !stepsData?.data) return [];

    const stepsById: Record<string, any> = {};
    for (const step of stepsData.data) {
      stepsById[String(step.id)] = step;
    }

    return versionsData.data
      .filter(
        (v: any) =>
          v.relationships?.["approval-steps"]?.data &&
          v.relationships["approval-steps"].data.length > 0
      )
      .map((version: any): VersionSection => {
        const stepRels: any[] =
          version.relationships?.["approval-steps"]?.data || [];

        const steps: ApprovalStepSection[] = stepRels
          .map((rel: any): ApprovalStepSection | null => {
            const step = stepsById[String(rel.id)];
            if (!step) return null;

            const stepApprovals = approvalsByStepId[String(step.id)] || [];
            const approvals: ApprovalRow[] = stepApprovals.map(
              (pa: any): ApprovalRow => ({
                id: String(pa.id),
                status: pa.attributes?.status || "",
                approverName: pa.attributes?.["approver-name"] || "",
                completedAt: pa.attributes?.["completed-at"] || null,
                reason: pa.attributes?.reason || "",
                comment: pa.attributes?.comment || "",
                canApprove: !!pa.links?.approvals,
                approvalId: String(pa.id),
              })
            );

            return {
              id: String(step.id),
              description: step.attributes?.description || "",
              status: step.attributes?.status || "",
              step: step.attributes?.step || "",
              approvals,
            };
          })
          .filter((s): s is ApprovalStepSection => s !== null);

        return {
          id: String(version.id),
          name: version.attributes?.name || "",
          steps,
        };
      });
  }, [versionsData, stepsData, approvalsByStepId]);

  const existingApprovalUserIds = useCallback(
    (stepId: string): Set<string> => {
      const stepApprovals = approvalsByStepId[stepId] || [];
      const ids = new Set<string>();
      for (const pa of stepApprovals) {
        const userId = pa.relationships?.user?.data?.id;
        if (userId) ids.add(String(userId));
      }
      return ids;
    },
    [approvalsByStepId]
  );

  const handleCreateApproval = useCallback(
    async (
      stepId: string,
      userId: string,
      status: string,
      lobIds: string[]
    ): Promise<boolean> => {
      try {
        await createProjectApproval({
          body: {
            data: {
              type: "project-approvals",
              attributes: {
                status: status as any,
                "lob-ids": lobIds,
              },
              relationships: {
                "approval-step": {
                  data: { type: "approval-steps", id: Number(stepId) },
                },
                user: {
                  data: { type: "users", id: Number(userId) },
                },
              },
            } as any,
          },
        }).unwrap();
        refetchSteps();
        return true;
      } catch {
        return false;
      }
    },
    [createProjectApproval, refetchSteps]
  );

  const buildApprovalBody = (approvalId: string, comment: string) => ({
    data: {
      type: "project-approvals",
      id: Number(approvalId),
      attributes: { comment },
    } as any,
  });

  const handleApproveApproval = useCallback(
    async (approvalId: string, comment: string): Promise<boolean> => {
      try {
        await approveApproval({
          id: String(approvalId),
          body: buildApprovalBody(approvalId, comment),
        }).unwrap();
        refetchSteps();
        return true;
      } catch {
        return false;
      }
    },
    [approveApproval, refetchSteps]
  );

  const handleDeclineApproval = useCallback(
    async (approvalId: string, comment: string): Promise<boolean> => {
      try {
        await declineApproval({
          id: String(approvalId),
          body: buildApprovalBody(approvalId, comment),
        }).unwrap();
        refetchSteps();
        return true;
      } catch {
        return false;
      }
    },
    [declineApproval, refetchSteps]
  );

  const handleCancelApproval = useCallback(
    async (approvalId: string, comment: string): Promise<boolean> => {
      try {
        await cancelApproval({
          id: String(approvalId),
          body: buildApprovalBody(approvalId, comment),
        }).unwrap();
        refetchSteps();
        return true;
      } catch {
        return false;
      }
    },
    [cancelApproval, refetchSteps]
  );

  const handleRescopeApproval = useCallback(
    async (approvalId: string, comment: string): Promise<boolean> => {
      try {
        await rescopeApproval({
          id: String(approvalId),
          body: buildApprovalBody(approvalId, comment),
        }).unwrap();
        refetchSteps();
        return true;
      } catch {
        return false;
      }
    },
    [rescopeApproval, refetchSteps]
  );

  const handleViewVersionChanges = useCallback(
    (versionId: string) => {
      window.location.assign(
        `${appHost}/projects/${projectId}/project_versions/${versionId}/edit`
      );
    },
    [appHost, projectId]
  );

  if (!authorized || versionsLoading || stepsLoading || usersLoading) {
    return <ScopeStackSpinner />;
  }

  return (
    <Show
      versions={versions}
      projectId={projectId}
      users={users}
      lobs={lobs}
      onApprove={handleApproveApproval}
      onDecline={handleDeclineApproval}
      onCancel={handleCancelApproval}
      onRescope={handleRescopeApproval}
      onViewVersionChanges={handleViewVersionChanges}
      onCreateApproval={handleCreateApproval}
      existingApprovalUserIds={existingApprovalUserIds}
      canCancel={canCancel}
    />
  );
};

export default ProjectApprovalsV2Mspa;
